import {CartIcon} from '@/components/svg/CartIcon';
import ColdIcon from '@/components/svg/ColdIcon';
import {CatalogueModale} from '@/domains/article/NomenclatureModale/CatalogueModale';
import {imageOptimisee} from '@/domains/mediatheque/optimisation';
import {ArticleCodesDisplay} from '@/domains/panier/components/ArticleCodesDisplay';
import {useCadencier, useClient} from '@/haligator-factory';
import {Box, HStack, Stack, Text, VStack} from '@chakra-ui/layout';
import {Image} from '@chakra-ui/react';
import NiceModal from '@ebay/nice-modal-react';
import {Result} from '@orama/orama';
import {HaliRenderOne} from '@synako/haligator';
import {State} from '@synako/halitrek';
import {CatalogueMarketplace, HitItemCatalogue} from 'api-types/ffconnect';
import {ItemCatalogue2Desc} from 'api-types/ffconnect-hal';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import {
  isItemStateCatalogueFF,
  isMarketplaceHit,
  useItemCatalogueOuMarketplace,
} from 'ff-common';

interface SearchResultItemComponentProps {
  item: Result<HitItemCatalogue>;
  index: number;
  itemLink: (code: string, marketplace_id?: string) => string;
  handleProductClick: (result: Result<HitItemCatalogue>, index: number) => void;
  handleCartIconClick: (
    code: string,
    multiple: number | undefined,
    result: Result<HitItemCatalogue>,
    index: number,
  ) => void;
  handleMarketplaceCartIconClick: (
    ref_tiers: string,
    marketplace_id: CatalogueMarketplace,
    itemState?: State<ItemCatalogue2Desc>,
  ) => void;
  handlePrecommandeAction: (itemState: State<ItemCatalogue2Desc>) => void;
}

export const SearchResultItemComponent: React.FC<
  SearchResultItemComponentProps
> = ({
  item,
  index,
  itemLink,
  handleProductClick,
  handleCartIconClick,
  handleMarketplaceCartIconClick,
  handlePrecommandeAction,
}) => {
  const client = useClient();
  const {state: itemState} = useItemCatalogueOuMarketplace(
    client,
    item.document,
  );

  const handleClick = () => {
    handleProductClick(item, index);
    NiceModal.remove(CatalogueModale);
  };

  // gére le cas d'un article en precommande ou non
  const handleIconClick = () => {
    if (isMarketplaceHit(item.document)) {
      handleMarketplaceCartIconClick(
        item.document.ref_tiers,
        item.document.marketplace_id,
        itemState,
      );
      return;
    }
    if (
      isItemStateCatalogueFF(itemState) &&
      itemState.data.tolede.precommande
    ) {
      handlePrecommandeAction(itemState);
    } else {
      handleCartIconClick(
        item.document.code,
        item.document.multiple_de_commande_client,
        item,
        index,
      );
    }
  };

  const productUrl = itemLink(
    item.document.ref_tiers ?? item.document.code,
    item.document.marketplace_id,
  );

  return (
    <HStack
      key={item.id}
      w='100%'
      justifyContent='space-between'
      spacing={0}
    >
      <Link
        to={productUrl}
        style={{width: 'calc(100% - 60px)', display: 'block'}}
      >
        <SearchResultItem
          item={item}
          onProductClick={handleClick}
        />
      </Link>
      <Box
        cursor='pointer'
        width='60px'
        height='100%'
        display='flex'
        justifyContent='center'
        alignItems='center'
        _hover={{transform: 'scale(1.2)'}}
        transition='transform 0.2s ease-in-out'
        onClick={handleIconClick}
      >
        <CartIcon
          color='gray.dark'
          height='24px'
          width='24px'
        />
      </Box>
    </HStack>
  );
};

const SearchResultItem: React.FC<{
  item: Result<HitItemCatalogue>;
  onProductClick: () => void;
}> = ({item, onProductClick}) => {
  const itemVignette = imageOptimisee(item.document.vignette_url, 40);
  const {t} = useTranslation();
  const cadencierNav = useCadencier();

  return (
    <Stack
      direction='row'
      px={4}
      py={2}
      cursor='pointer'
      _hover={{bg: 'gray.100'}}
      alignItems='center'
      spacing={3}
      onClick={onProductClick}
      position='relative'
    >
      <VStack
        flexShrink={0}
        boxSize='40px'
      >
        <Image
          src={itemVignette}
          alt={item.document.libelle_commercial}
          maxHeight='40px'
          maxWidth='40px'
        />
      </VStack>
      {item.document.temperature_livraison === 'negatif' && (
        <Box
          position='absolute'
          bottom='3'
          left='2'
        >
          <ColdIcon boxSize='13px' />
        </Box>
      )}
      <VStack
        alignItems='start'
        spacing={0}
        flex={1}
      >
        <HStack>
          <ArticleCodesDisplay
            code_article={item.document.code}
            marketplace_id={item.document.marketplace_id}
            toledeData={
              item.document.code_article_client || item.document.code_generique
                ? item.document
                : undefined
            }
            size='2xs'
          />
          {item.document.nature_du_prix === 'promo' && (
            <Text
              fontSize='2xs'
              fontStyle='initial'
              color='red.500'
            >
              {t('global.promotion')}
            </Text>
          )}
          <HaliRenderOne
            nav={cadencierNav}
            render={({data: cadencier}) => {
              const articleDejaPresentCadencier = cadencier.tri_selection.some(
                (g) => g.articles.find((a) => a.code === item.document.code),
              );

              return articleDejaPresentCadencier ? (
                <Text
                  fontSize='2xs'
                  fontStyle='initial'
                  color='blue.main'
                >
                  {t('global.cadencier')}
                </Text>
              ) : null;
            }}
          />
        </HStack>
        <Text
          fontSize='xs'
          fontWeight='700'
        >
          {item.document.libelle_commercial}
        </Text>
        <Text fontSize='xs'>{item.document.marque}</Text>
      </VStack>
    </Stack>
  );
};
